/**
 * Happy Pay campaign configuration.
 *
 * Everything a campaign owner changes between runs lives here. Nothing in this
 * file is implementation tuning — reel geometry, marquee row caps and sampling
 * limits stay next to the code that uses them, because mixing "how many prizes
 * there are" with "how many DOM rows the marquee renders" makes both harder to
 * find.
 */
/**
 * Soft-launch switch for the Happy Pay campaign.
 *
 * While false, nothing on the public site links to it: the header and sidebar
 * entries, the home page hero slide, and the Happy Pay option in the login
 * chooser are all withheld. The routes stay live, so anyone who knows them can
 * still open `/infohappy`, `/dashboardhappy` and `/login?link=1` directly — the
 * point is that no visitor arrives there by clicking.
 *
 * Set to true to launch. That is the only change needed; nothing is commented
 * out, so there is no second edit to forget.
 */
export const HAPPY_PAY_PUBLIC = true;

/**
 * Maintenance switch for customer login.
 *
 * While true, the header and mobile sidebar have no "Нэвтрэх" button, the
 * login page has no "ДАН нэвтрэх" button and no message about
 * it, and a DAN callback (`/login?reg=…&reff=…`) is silently ignored rather
 * than creating a session. Sessions that already exist are left alone.
 *
 * Set back to false to reopen login.
 */
export const LOGIN_CLOSED = true;

export const CAMPAIGN = {
    /**
     * One entry per draw. A phase owns everything specific to that event — its
     * name, prize split, dates and earning rules — so next year's campaign is a
     * new object appended here rather than edits scattered across the codebase.
     *
     *   name       heading on the live draw screen
     *   winners    prize quota per gender; the total is derived, never restated
     *   meritWinners
     *              employee merit winners: ticket codes awarded on merit rather
     *              than by chance, in the order listed. Held back until a third
     *              of the prizes are gone, and only awarded while that ticket's
     *              gender still has a slot free, so a merit seat can never
     *              overrun a quota. Omit or leave empty for a draw wholly by
     *              chance.
     *   opensAt    the window opens
     *   revealsAt  the draw happens; the countdown counts toward this
     *   closesAt   the window closes, INCLUSIVE of this whole day
     *   earning    how tickets accrue during this phase. A route set to
     *              tickets: 0 is not offered that phase and its card is hidden.
     *
     * ⚠ `earning` MIRRORS THE BACKEND, one phase per backend iteration. The
     *   authoritative copy is campaigns/happy-pay-cust.php →
     *   LOTTERY_ITERATIONS[n]['rules'], which is what actually computes
     *   `lotteryCount`. These values only drive the wording on the dashboard
     *   cards; if they drift, the cards explain a total the server did not
     *   calculate that way. Keep phase ids aligned with backend iteration ids.
     *
     * These are the 2025 dates and all lie in the past, which is why no
     * countdown runs and the ticket list stays gated. The backend's own
     * iteration 2 is 2026-08-01 → 2026-08-31.
     */
    phases: [
        // Historical. Kept so a past draw still resolves its own name and rules.
        {
            id: 1,
            name: '2025 MAMA AWARDS тохирол',
            winners: {male: 4, female: 5},
            // The merit seat this draw was run with. Kept rather than blanked so
            // the record of that event stays accurate.
            meritWinners: ['ACNCNEZEL'],
            opensAt: '2025-10-22',
            revealsAt: '2025-11-11',
            closesAt: '2025-12-01',
            earning: {
                transactions: {per: 5, tickets: 3},
                referrals: {per: 3, tickets: 5},
                cardLinked: {tickets: 10},
                promoCode: {tickets: 5},
            },
        },

        // 2026 campaign: two draws of 6 winners = 12.
        // Тохирол 1 — drawn 09.04. closesAt equals Тохирол 2's opensAt (09.07):
        // the windows meet with no gap and no overlap, so тохирол-1 results are
        // viewable 09.04–09.07 and then gated until the тохирол 2 draw.
        {
            id: 2,
            name: '2026 MAMA AWARDS тохирол 1',
            winners: {male: 3, female: 3},
            meritWinners: ['NDL2ZSPZT', '9MHZTSVJX'],
            opensAt: '2026-08-11',
            revealsAt: '2026-09-04',
            closesAt: '2026-09-07',
            earning: {
                // Values are the published info-page terms:
                //   3 гүйлгээ тутамд 1 эрх · найз тутамд 3 эрх · карт 5 эрх
                transactions: {per: 3, tickets: 1},
                referrals: {per: 1, tickets: 3},
                cardLinked: {tickets: 5},
                promoCode: {tickets: 0},
            },
        },

        // Тохирол 2 — earning 09.07–10.08, drawn 10.09, results viewable through
        // 10.16. opensAt is the only thing that moves the Тохирол 1 → Тохирол 2
        // boundary (a day belongs to whichever phase opened most recently) and it
        // equals Тохирол 1's closesAt so the windows meet with no gap. The
        // backend's getCurrentIteration() MUST use this same 09.07 start, or the
        // draw screen and the server will disagree on which iteration is live.
        {
            id: 3,
            name: '2026 MAMA AWARDS тохирол 2',
            winners: {male: 1, female: 5},
            meritWinners: ['GBGWRRUKX','YHW7RAYGS'],
            opensAt: '2026-09-07',
            revealsAt: '2026-10-09',
            closesAt: '2026-10-16',
            earning: {
                // Values are the published info-page terms:
                //   3 гүйлгээ тутамд 1 эрх · найз тутамд 3 эрх · карт 5 эрх
                transactions: {per: 3, tickets: 1},
                referrals: {per: 1, tickets: 3},
                cardLinked: {tickets: 5},
                promoCode: {tickets: 0},
            },
        },
    ],

    /** Pacing of the on-stage draw, in seconds. Not phase-specific. */
    draw: {
        spinDurationSeconds: 5,
        marqueeScrollSeconds: 100,
    },
};
